import type { ProjectLifecycleCommand } from "@kollectivia/shared";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiException } from "../../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../../lib/apiErrorMessage";
import {
  useProjectLifecycle,
  useProjectLifecycleCommand,
} from "../../../lib/queries/projectLifecycle";
import { useArchiveProject, useRestoreProject } from "../../../lib/queries/projects";

const button =
  "min-h-11 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-600 disabled:opacity-50";
type Action = ProjectLifecycleCommand["action"] | "archive" | "unarchive";
export function ProjectLifecycleSection({ projectId }: { projectId: string }) {
  const { t, i18n } = useTranslation();
  const query = useProjectLifecycle(projectId);
  const command = useProjectLifecycleCommand(projectId);
  const archive = useArchiveProject();
  const restore = useRestoreProject(projectId);
  const [choice, setChoice] = useState<{ action: Action; version: number } | null>(null);
  const [reason, setReason] = useState("");
  const input = useRef<HTMLTextAreaElement>(null);
  const confirmationHeading = useRef<HTMLHeadingElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const restoreFocus = useRef(false);
  const pending = command.isPending || archive.isPending || restore.isPending;
  const error = command.error || archive.error || restore.error || query.error;
  useEffect(() => {
    if (choice) (input.current ?? confirmationHeading.current)?.focus();
    else if (restoreFocus.current) {
      heading.current?.focus();
      restoreFocus.current = false;
    }
  }, [choice]);
  const close = () => {
    setChoice(null);
    setReason("");
    restoreFocus.current = true;
  };
  const start = (action: Action) => {
    if (!query.data) return;
    command.reset();
    archive.reset();
    restore.reset();
    setReason("");
    setChoice({ action, version: query.data.version });
  };
  const perform = async () => {
    if (!choice) return;
    try {
      if (choice.action === "archive") await archive.mutateAsync(projectId);
      else if (choice.action === "unarchive") await restore.mutateAsync();
      else
        await command.mutateAsync({
          action: choice.action,
          reason,
          expected_version: choice.version,
        });
      close();
    } catch {
      /* Mutations retain the bilingual error and the entered reason. */
    }
  };
  const v = query.data;
  return (
    <section
      id="project-lifecycle"
      tabIndex={-1}
      aria-labelledby="project-lifecycle-title"
      className="my-4 rounded-xl border border-slate-200 bg-white p-4"
    >
      <h2
        id="project-lifecycle-title"
        ref={heading}
        tabIndex={-1}
        className="text-lg font-semibold"
      >
        {t("project_lifecycle.title")}
      </h2>
      {error && (
        <p role="alert" className="mt-2 text-red-700">
          {error instanceof ApiException
            ? getApiErrorMessage(error, normalizeLang(i18n.language))
            : t("errors.generic")}
        </p>
      )}
      {!v ? (
        <p>{t("app.loading")}</p>
      ) : (
        <>
          <p className="mt-2 font-medium">
            {t(`project_lifecycle.states.${v.state}`)}
            {v.archived ? ` · ${t("project_lifecycle.archived")}` : ""}
          </p>
          {v.reason && <p className="mt-1 whitespace-pre-wrap break-words">{v.reason}</p>}
          <p className="mt-2 text-sm text-slate-600">{t("project_lifecycle.help")}</p>
          {v.in_flight && (
            <p role="status" className="mt-2 text-amber-800">
              {t("project_lifecycle.in_flight")}
            </p>
          )}
          {!choice && (
            <div className="mt-3 flex flex-wrap gap-2">
              {v.can_mark_lost && (
                <button className={button} onClick={() => start("lost")}>
                  {t("project_lifecycle.actions.lost")}
                </button>
              )}
              {v.can_stop && (
                <button className={button} onClick={() => start("stop")}>
                  {t("project_lifecycle.actions.stop")}
                </button>
              )}
              {v.can_reopen && (
                <button className={button} onClick={() => start("reopen")}>
                  {t("project_lifecycle.actions.reopen")}
                </button>
              )}
              {v.can_archive && (
                <button className={button} onClick={() => start("archive")}>
                  {t("project_lifecycle.actions.archive")}
                </button>
              )}
              {v.can_unarchive && (
                <button className={button} onClick={() => start("unarchive")}>
                  {t("project_lifecycle.actions.unarchive")}
                </button>
              )}
            </div>
          )}
          {choice && (
            <form
              className="mt-4 space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                void perform();
              }}
              onKeyDown={(e) => {
                if (e.key === "Escape" && !pending) {
                  e.preventDefault();
                  close();
                }
              }}
            >
              <h3 ref={confirmationHeading} tabIndex={-1} className="font-semibold">
                {t(`project_lifecycle.actions.${choice.action}`)}
              </h3>
              <p>
                {t(
                  choice.action === "reopen"
                    ? "project_lifecycle.reopen_warning"
                    : choice.action === "archive" || choice.action === "unarchive"
                      ? "project_lifecycle.archive_warning"
                      : "project_lifecycle.close_warning",
                  {
                    requests: v.active_requests,
                    followups: v.client_followups,
                    messages: v.queued_messages,
                  },
                )}
              </p>
              {choice.action !== "archive" && choice.action !== "unarchive" && (
                <label className="block">
                  {t("project_lifecycle.reason")}
                  <textarea
                    ref={input}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    required
                    maxLength={500}
                    disabled={pending}
                    className="mt-1 block w-full rounded-lg border border-slate-300 p-2"
                  />
                </label>
              )}
              <div className="flex flex-wrap gap-2">
                <button type="button" className={button} disabled={pending} onClick={close}>
                  {t("common.cancel")}
                </button>
                <button
                  className={button}
                  type="submit"
                  disabled={
                    pending ||
                    (!reason.trim() && choice.action !== "archive" && choice.action !== "unarchive")
                  }
                >
                  {pending ? t("app.loading") : t("project_lifecycle.confirm")}
                </button>
              </div>
            </form>
          )}
          {v.history.length > 0 && (
            <details className="mt-3">
              <summary className="min-h-11 cursor-pointer py-2">
                {t("project_lifecycle.history")}
              </summary>
              <ol className="space-y-2">
                {v.history.map((e) => (
                  <li key={e.version} className="break-words text-sm">
                    <strong>{t(`project_lifecycle.actions.${e.action}`)}</strong> — {e.reason}
                    <time className="ml-2 text-slate-600" dateTime={e.created_at}>
                      {new Date(e.created_at).toLocaleString(i18n.language)}
                    </time>
                  </li>
                ))}
              </ol>
            </details>
          )}
        </>
      )}
    </section>
  );
}
